import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, QrCode, ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow, StatusBadge } from "@/components/ui-bits";
import CertificateViewer from "@/components/CertificateViewer";
import { api } from "@/lib/api";
import { IMG } from "@/data/images";

export default function Verify() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("bb") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const runVerify = async (value) => {
    const v = (value ?? query).trim();
    if (!v) return;
    setLoading(true); setSearched(true);
    try {
      const { data } = await api.get(`/verify/${encodeURIComponent(v)}`);
      setResult(data);
    } catch {
      setResult({ found: false, status: "unable", status_label: "Unable to verify", message: "No record could be retrieved." });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("bb")) runVerify(params.get("bb")); }, []); // eslint-disable-line

  return (
    <div>
      <section className="relative pt-40 pb-16 overflow-hidden grain">
        <div className="absolute inset-0 radial-blue" />
        <img src={IMG.diamondReflection} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.1]" />
        <div className="absolute inset-0 bg-gradient-to-b from-bb-bg/70 to-bb-bg" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <Reveal><Eyebrow className="justify-center">Public Verification</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">Verify a Botswana diamond</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-bb-silver/70">Enter a Botswana Brilliance number or scan the certificate QR code.</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9 glass rounded-full p-1.5 flex items-center gap-2 max-w-xl mx-auto">
              <Search className="h-5 w-5 text-bb-silver/50 ml-4" strokeWidth={1.3} />
              <input
                data-testid="verify-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runVerify()}
                placeholder="e.g. BB-BW-2026-000117"
                className="flex-1 bg-transparent outline-none text-bb-white placeholder:text-bb-silver/40 py-3 tracking-wide font-mono text-sm"
              />
              <button data-testid="verify-submit-btn" onClick={() => runVerify()} className="btn-gold rounded-full px-6 py-2.5 text-sm">
                {loading ? "Checking…" : "Verify"}
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-5 flex flex-wrap justify-center gap-2 text-xs text-bb-silver/45">
              <span>Try:</span>
              {["BB-BW-2026-000117", "BB-BW-2026-000142", "BB-BW-2026-000098"].map((s) => (
                <button key={s} onClick={() => { setQuery(s); runVerify(s); }} className="font-mono text-bb-gold/80 hover:text-bb-gold underline underline-offset-2">
                  {s}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24">
        <AnimatePresence mode="wait">
          {searched && !loading && result && (
            <motion.div key={result.bb_number || "none"} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              {result.found ? (
                <div className="space-y-8">
                  <ResultBanner status={result.status} label={result.status_label} bb={result.bb_number} />
                  <CertificateViewer cert={result} />
                </div>
              ) : (
                <ResultBanner status="unable" label={result.status_label} message={result.message} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}

const STATUS_EXPLANATIONS = {
  verified: "This certificate is active and valid. The digital record exists, matches the supplied identifier, and its evidence has been reviewed and connected. You can rely on this record.",
  verification_pending: "This diamond's records have been submitted but the review is not yet complete. A certificate exists but is not yet confirmed as verified, please do not rely on it until its status shows Verified.",
  ownership_transfer_pending: "An ownership or custody transfer is being recorded for this diamond. The record is temporarily in transition, treat it as not fully confirmed until the transfer completes.",
  record_updated: "This record has recently been updated and the new details are being finalised. Check back shortly for the confirmed status.",
  suspended: "This certificate was previously active but has been temporarily placed on hold and is NOT currently valid for trust. This can happen when a concern is reported, a custody or ownership issue is being reviewed, or an investigation is underway. Please do not rely on this diamond until the certificate is restored to Verified.",
  unable: "No valid record could be retrieved for this identifier. Check the Botswana Brilliance number, or the diamond may not be registered on the platform.",
};

const TONES = {
  positive: { ring: "border-emerald-400/40 bg-emerald-400/10", icon: "text-emerald-300", Icon: ShieldCheck },
  warning: { ring: "border-amber-400/40 bg-amber-400/10", icon: "text-amber-300", Icon: ShieldQuestion },
  negative: { ring: "border-rose-400/40 bg-rose-400/10", icon: "text-rose-300", Icon: ShieldAlert },
};

function toneFor(status) {
  if (status === "verified") return "positive";
  if (["verification_pending", "ownership_transfer_pending", "record_updated"].includes(status)) return "warning";
  return "negative";
}

function ResultBanner({ status, label, bb, message }) {
  const { ring, icon, Icon } = TONES[toneFor(status)];
  const explanation = message || STATUS_EXPLANATIONS[status] || "This digital record exists and matches the supplied identifier. Botswana Brilliance verifies that the record exists and does not make claims unsupported by the available evidence.";
  return (
    <div data-testid="verify-result-banner" className="glass rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
      <div className={`grid place-items-center h-16 w-16 rounded-full border ${ring}`}>
        <Icon className={icon} strokeWidth={1.2} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <h2 className="font-serif text-3xl">{label}</h2>
          <StatusBadge status={status} label={label} />
        </div>
        {bb && <div className="font-mono text-sm text-bb-gold mt-1">{bb}</div>}
        <p data-testid="verify-status-explanation" className="text-sm text-bb-silver/60 mt-2 max-w-2xl">
          {explanation}
        </p>
      </div>
    </div>
  );
}
